/**
 * Centralized storage key definitions for localStorage and IndexedDB.
 *
 * @module storageKeys
 *
 * All storage keys used throughout the application are defined here to:
 * - Prevent key collisions and typos
 * - Enable easy refactoring and key management
 * - Provide type-safe access to storage keys
 * - Document all persisted data locations
 */

/**
 * Storage keys for application data persistence.
 * All keys are prefixed with 'strive_' to prevent conflicts with other applications.
 */
export const STORAGE_KEYS = {
  /**
   * Main nutrition data storage key.
   * Stores array of DailyNutrition entries.
   */
  NUTRITION_DATA: 'strive_nutrition_data',

  /**
   * Body measurement data storage key.
   * Stores array of BodyMeasurementEntry entries.
   */
  BODY_MEASUREMENTS: 'strive_body_measurements',

  /**
   * User's theme preference (light/dark mode).
   * Stores 'light' | 'dark' string value.
   */
  THEME_PREFERENCE: 'strive_theme_preference',

  /**
   * Weekly nutrition goal settings.
   * Stores NutritionGoals configuration object.
   */
  NUTRITION_GOALS: 'strive_nutrition_goals',

  /**
   * Body measurement chart category visibility settings.
   * Stores object with measurement field keys and boolean visibility flags.
   */
  CHART_CATEGORIES: 'strive_chart_categories',

  /**
   * Chart zoom state for body measurements visualization.
   * Stores zoom domain [start, end] coordinates.
   */
  CHART_ZOOM_BODY_MEASUREMENTS: 'strive_chart_zoom_body_measurements',

  /**
   * Storage version for data migration tracking.
   * Stores numeric version identifier.
   */
  STORAGE_VERSION: 'strive_storage_version',
} as const

/**
 * Type-safe storage key type.
 * Ensures only valid storage keys can be used in type signatures.
 */
export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]

/**
 * Type guard to check if a string is a valid storage key.
 *
 * @param key - The string to check
 * @returns True if the key is a valid StorageKey
 *
 * @example
 * ```typescript
 * const key = 'strive_nutrition_data'
 * if (isStorageKey(key)) {
 *   // TypeScript knows key is StorageKey here
 *   const data = localStorage.getItem(key)
 * }
 * ```
 */
export function isStorageKey(key: string): key is StorageKey {
  return Object.values(STORAGE_KEYS).includes(key as StorageKey)
}

/**
 * Get all storage keys as an array.
 * Useful for bulk operations like clearing all app data.
 *
 * @returns Array of all storage key values
 *
 * @example
 * ```typescript
 * // Clear all app data
 * getAllStorageKeys().forEach(key => localStorage.removeItem(key))
 * ```
 */
export function getAllStorageKeys(): StorageKey[] {
  return Object.values(STORAGE_KEYS)
}

/**
 * Clear all application storage keys from localStorage.
 * This does not affect other applications' data.
 *
 * @example
 * ```typescript
 * // Reset app to initial state
 * clearAllAppStorage()
 * ```
 */
export function clearAllAppStorage(): void {
  getAllStorageKeys().forEach((key) => {
    localStorage.removeItem(key)
  })
}
